import {
  AudienceInstance,
  AudienceType,
  ContentTypes,
  MatchingCriteria,
  PartnerPermissionTypes,
  Workflow,
  WorkflowResponse,
  WorkflowResultGroup,
  WorkflowType,
} from '@/client/services/api/graphql/gql/graphql';
import {
  Avatar,
  Box,
  Circle,
  Collapse,
  Grid,
  GridItem,
  HStack,
  Icon,
  Stack,
  Tag,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {
  CalendarWeek,
  HeavyCheck,
  HeavyClose,
  TrackArticle,
  TrackAssessment,
  TrackFile,
  TrackScorm,
  TrackVideo,
} from '@/client/components/icons/ContinuIcons';
import { IoGitNetworkOutline, IoLocationOutline } from 'react-icons/io5';

import { GoOrganization } from 'react-icons/go';
import { HiOutlineUserGroup } from 'react-icons/hi';
import { IconType } from 'react-icons/lib';
import { LiaUserGraduateSolid } from 'react-icons/lia';
import NoNotificationsPlaceholder from '../notifications/NoNotificationsPlaceholder';
import NotificationsListItem from '../notifications/NotificationsListItem';
import OutlineButton from '@/client/components/shared/buttons/OutlineButton';
import { useGenerateAutomationInformation } from '@/client/components/admin/assignments/hooks/useGenerateAutomationinformation';
import { useTranslation } from 'react-i18next';

function Section({
  label,
  backgroundColor,
  children,
}: {
  label: string;
  backgroundColor: string;
  children: React.ReactNode;
}) {
  return (
    <Grid
      templateColumns="repeat(2, 1fr)"
      backgroundColor={backgroundColor}
      borderBottom="1px solid"
      borderBottomColor="neutral.100"
      paddingY={8}
    >
      <GridItem colSpan={1} paddingLeft={9}>
        <Text fontSize="16px" fontWeight={600}>
          {label}
        </Text>
      </GridItem>

      <GridItem colSpan={1}>{children}</GridItem>
    </Grid>
  );
}

function ContentTag({ item }: { item: WorkflowResponse }) {
  const objectTypeIconMap: Record<string, React.ElementType> = {
    [ContentTypes.Article]: TrackArticle,
    [ContentTypes.Assessment]: TrackAssessment,
    [ContentTypes.File]: TrackFile,
    [ContentTypes.Scorm]: TrackScorm,
    [ContentTypes.Video]: TrackVideo,
    [ContentTypes.Track]: TrackArticle,
    [ContentTypes.Journey]: TrackArticle,
    [ContentTypes.ImportedContent]: TrackArticle,
    [ContentTypes.Event]: TrackArticle,
    [ContentTypes.Poll]: TrackArticle,
    [ContentTypes.Presentation]: TrackArticle,
    [ContentTypes.Quiz]: TrackArticle,
    [ContentTypes.Workshop]: TrackArticle,
    [ContentTypes.Media]: TrackArticle,
    [ContentTypes.MultiSessionWorkshop]: TrackArticle,
    [ContentTypes.MultiSessionWorkshopSession]: TrackArticle,
  };

  return (
    <Tag variant="create">
      <Icon as={objectTypeIconMap[item.content?.contentType as string]} />

      {item.content?.title}
    </Tag>
  );
}

function AudienceTag({ item }: { item: AudienceInstance }) {
  const typeIconMap: { [key: string]: IconType } = {
    location: IoLocationOutline,
    department: IoGitNetworkOutline,
    team: HiOutlineUserGroup,
    org_level: GoOrganization,
    grade: LiaUserGraduateSolid,
    group: HiOutlineUserGroup,
  };

  return (
    <Tag variant="create">
      {item.type === AudienceType.User && (
        <Avatar
          marginRight={2}
          size="xs"
          name={item.name}
          src={item.image as string}
          backgroundColor="warmNeutral.200"
          color="warmNeutral.1200"
        />
      )}

      {item.type === AudienceType.Segment && (
        <Circle size="20px" backgroundColor="warmNeutral.200" marginRight={2}>
          <Icon as={typeIconMap[item.segmentationType]} />
        </Circle>
      )}

      {item.name}
    </Tag>
  );
}

function PreviewCollapse({
  renderButton,
  children,
}: {
  renderButton: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { isOpen, onToggle } = useDisclosure();

  return (
    <Stack>
      <Collapse startingHeight={50} in={isOpen}>
        {children}
      </Collapse>

      {renderButton && (
        <OutlineButton width="fit-content" onClick={onToggle}>
          {isOpen
            ? t('admin.assignments.details.showLess')
            : t('admin.assignments.details.showAll')}
        </OutlineButton>
      )}
    </Stack>
  );
}

interface AssignmentDetailsViewProps {
  data: Workflow | WorkflowResultGroup;
}

export default function AssignmentDetailsView({ data }: AssignmentDetailsViewProps) {
  const { t } = useTranslation();
  const { generateAutomationInformation } = useGenerateAutomationInformation();

  const { audience, settings } = data;

  const {
    responses,
    nonReminderMessages,
    reminderMessages,
    overdueMessages,
    daysToComplete,
    requireRecompletion,
    escalateManager,
    escalateBuddy,
    escalationContacts,
    type,
    conditionals,
    triggerObject,
    assignmentOptions,
    dueFromHired,
  } = settings;

  const { delay } = assignmentOptions;

  const renderAutomationSection = type !== WorkflowType.PointInTime;

  const matchAnyAudience = audience.filter(
    (item) => item.matchingCriteria === MatchingCriteria.MatchAny,
  );
  const matchAllAudience = audience.filter(
    (item) =>
      item.matchingCriteria === MatchingCriteria.MatchAll &&
      item.type !== AudienceType.HiredDate &&
      item.type !== AudienceType.PartnerType,
  );
  const matchNoneAudience = audience.filter(
    (item) => item.matchingCriteria === MatchingCriteria.MatchNone,
  );

  const allNotifications = [...reminderMessages, ...nonReminderMessages, ...overdueMessages];

  const hasHiredDateFilter = audience.some((a) => a.type === AudienceType.HiredDate);

  const hasPartnerTypeFilter = audience.some((a) => a.type === AudienceType.PartnerType);

  const partnerPermissionItems = audience.filter((a) => a.type === AudienceType.PartnerType);

  const hasPartner = partnerPermissionItems.some(
    (item) => item.id === PartnerPermissionTypes.Partner,
  );
  const hasTenant = partnerPermissionItems.some(
    (item) => item.id === PartnerPermissionTypes.Tenant,
  );

  const getPartnerPermissionType = () => {
    if (hasPartner && hasTenant) {
      return PartnerPermissionTypes.PartnerAndTenant;
    }
    if (hasPartner) {
      return PartnerPermissionTypes.Partner;
    }
    if (hasTenant) {
      return PartnerPermissionTypes.Tenant;
    }
    return PartnerPermissionTypes.Partner;
  };

  const partnerPermissionTypeMap = {
    [PartnerPermissionTypes.Tenant]: t(
      'admin.assignments.audience.extend.extendAccess.instanceOnly',
    ),
    [PartnerPermissionTypes.Partner]: t(
      'admin.assignments.audience.extend.extendAccess.allExtendAccounts',
    ),
    [PartnerPermissionTypes.PartnerAndTenant]: t(
      'admin.assignments.audience.extend.extendAccess.bothExtendAndInstance',
    ),
  };

  const sections = [
    {
      label: t('admin.assignments.step.audience'),
      children: (
        <Stack spacing={6}>
          {(hasHiredDateFilter || hasPartnerTypeFilter) && (
            <Stack spacing={4}>
              <Text variant="createLabel" color="neutral.700">
                {t('admin.assignments.audienceFilters')}
              </Text>

              {hasPartnerTypeFilter && (
                <HStack>
                  <HeavyCheck />

                  <Text variant="createLabel">
                    {t('admin.assignments.audience.extend.extendAccess')}{' '}
                    {partnerPermissionTypeMap[getPartnerPermissionType()]}
                  </Text>
                </HStack>
              )}

              {hasHiredDateFilter && (
                <HStack>
                  <HeavyCheck />

                  <Text variant="createLabel">
                    {t('admin.assignments.audience.additionalFilters.hiredWithinDays', {
                      days: audience.find((a) => a.type === AudienceType.HiredDate)?.days || '#',
                    })}
                  </Text>
                </HStack>
              )}
            </Stack>
          )}

          {type !== WorkflowType.PointInTime && audience.length === 0 && (
            <Text variant="createLabel">
              {t('admin.assignments.step.audience.totalCount.automation')}
            </Text>
          )}

          {matchAnyAudience.length > 0 && (
            <Stack spacing={4}>
              <Text variant="createLabel">
                {t('admin.assignments.details.audience.matchAnyLabel')}
              </Text>

              <PreviewCollapse renderButton={matchAnyAudience.length > 6}>
                <HStack flexWrap="wrap">
                  {matchAnyAudience.map((item) => (
                    <AudienceTag key={item.id} item={item} />
                  ))}
                </HStack>
              </PreviewCollapse>
            </Stack>
          )}

          {matchAllAudience.length > 0 && (
            <Stack>
              <Text variant="createLabel">
                {t('admin.assignments.details.audience.matchAllLabel')}
              </Text>

              <PreviewCollapse renderButton={matchAllAudience.length > 6}>
                <HStack flexWrap="wrap">
                  {matchAllAudience.map((item) => (
                    <AudienceTag key={item.id} item={item} />
                  ))}
                </HStack>
              </PreviewCollapse>
            </Stack>
          )}

          {matchNoneAudience.length > 0 && (
            <Stack>
              <Text variant="createLabel">
                {t('admin.assignments.details.audience.excludeAnyLabel')}
              </Text>

              <PreviewCollapse renderButton={matchNoneAudience.length > 5}>
                <HStack flexWrap="wrap">
                  {matchNoneAudience.map((item) => (
                    <AudienceTag key={item.id} item={item} />
                  ))}
                </HStack>
              </PreviewCollapse>
            </Stack>
          )}
        </Stack>
      ),
    },
    {
      label: t('admin.assignments.step.content'),
      children: (
        <PreviewCollapse renderButton={responses.length > 5}>
          <HStack flexWrap="wrap">
            {responses.map((item) => (
              <ContentTag key={item.content?.id} item={item} />
            ))}
          </HStack>
        </PreviewCollapse>
      ),
    },
    {
      label: t('admin.assignments.step.settings'),
      children: (
        <Stack>
          {daysToComplete && daysToComplete > 0 && (
            <HStack marginY={4}>
              <CalendarWeek />

              <Text fontSize="14px" fontWeight={500}>
                {dueFromHired
                  ? t('admin.assignments.settings.preview.dueInDaysFromHired', {
                      days: daysToComplete,
                    })
                  : t('admin.assignments.settings.preview.dueInDaysFromAssigned', {
                      days: daysToComplete,
                    })}
              </Text>
            </HStack>
          )}

          {delay && (
            <HStack marginY={4}>
              <HeavyCheck />

              <Text fontSize="14px" fontWeight={500}>
                {t('admin.assignments.settings.preview.delayAssignment', { delay })}
              </Text>
            </HStack>
          )}

          <HStack marginY={4}>
            {requireRecompletion ? <HeavyCheck /> : <HeavyClose />}

            <Text variant="createLabel">
              {t('admin.assignments.settings.preview.requireUniqueCompletions')}
            </Text>
          </HStack>

          <HStack marginY={4}>
            {escalateManager ? <HeavyCheck /> : <HeavyClose />}

            <Text variant="createLabel">
              {t('admin.assignments.settings.preview.escalateToManager')}
            </Text>
          </HStack>

          <HStack marginY={4}>
            {escalateBuddy ? <HeavyCheck /> : <HeavyClose />}

            <Text variant="createLabel">
              {t('admin.assignments.settings.preview.escalateToBuddy')}
            </Text>
          </HStack>

          <HStack marginY={4}>
            {escalationContacts && escalationContacts.length > 0 ? <HeavyCheck /> : <HeavyClose />}

            <Text variant="createLabel">
              {t('admin.assignments.settings.preview.addEscalationContacts')}
            </Text>
          </HStack>
        </Stack>
      ),
    },
    {
      label: t('admin.assignments.step.notifications'),
      children: (
        <Stack>
          {allNotifications.length === 0 && (
            <Box marginRight={8}>
              <NoNotificationsPlaceholder />
            </Box>
          )}

          {allNotifications.length > 0 &&
            allNotifications.map((notification, index) => {
              const id = 'message' in notification ? notification.message.id : notification.id;

              return (
                <NotificationsListItem
                  key={id}
                  variant="neutral"
                  notification={notification}
                  renderDivider={index < allNotifications.length - 1}
                />
              );
            })}
        </Stack>
      ),
    },
  ];

  if (data.__typename !== 'WorkflowResultGroup' && renderAutomationSection) {
    sections.unshift({
      label: t('admin.assignments.step.automations'),
      children: (
        <Text maxWidth="80%" variant="createLabel">
          {generateAutomationInformation(type, triggerObject)}

          {conditionals && conditionals.length > 0 && (
            <Text as="span">
              {` (${conditionals.map((conditional) => conditional.name).join(', ')})`}
            </Text>
          )}
        </Text>
      ),
    });
  }

  return (
    <Stack spacing={0}>
      {sections.map((section, index) => (
        <Section
          key={section.label}
          label={section.label}
          backgroundColor={index % 2 === 0 ? 'neutral.0' : 'white'}
        >
          {section.children}
        </Section>
      ))}
    </Stack>
  );
}
